import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { ClipboardList, LogOut, Menu, X, Building2, MapPin, Wrench, ChevronDown, ChevronRight, History, BarChart3, Tag, Truck, Users, AlertTriangle, LayoutDashboard, Grid3X3, Repeat } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const navItems = [
  { name: 'Dashboard', href: '/planificador', icon: LayoutDashboard },
  { name: 'Actividades', href: '/planificador/actividades', icon: ClipboardList },
  { name: 'Carta Gantt', href: '/planificador/gantt', icon: BarChart3 },
  {
    name: 'Mantenedores', icon: Wrench, isGroup: true,
    children: [
      { name: 'Areas', href: '/planificador/areas', icon: Building2 },
      { name: 'Sectores', href: '/planificador/sectores', icon: MapPin },
      { name: 'Tipos Actividad', href: '/planificador/tipos-actividad', icon: Tag },
      { name: 'Recursos', href: '/planificador/recursos', icon: Truck },
      { name: 'Personas', href: '/planificador/personas', icon: Users },
      { name: 'Riesgos', href: '/planificador/riesgos', icon: AlertTriangle },
    ]
  },
  { name: 'Registro de Acciones', href: '/planificador/registro', icon: History },
];

export const PlanificadorLayout = ({ children }) => {
  const { user, logout, token, hasMultiplePerfiles, switchPerfil } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState({});
  const [logoImage, setLogoImage] = useState(() => localStorage.getItem('cached_logo_url'));

  useEffect(() => {
    const fetchLogo = async () => {
      try {
        const res = await axios.get(`${API}/configuracion`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.data.logo_image) {
          let imgUrl = res.data.logo_image;
          if (!imgUrl.startsWith('http') && !imgUrl.startsWith('data:'))
            imgUrl = `${process.env.REACT_APP_BACKEND_URL}${imgUrl}`;
          setLogoImage(imgUrl);
          localStorage.setItem('cached_logo_url', imgUrl);
        }
      } catch {}
    };
    if (!logoImage) fetchLogo();
  }, [token, logoImage]);

  return (
    <div className="min-h-screen bg-slate-50">
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/30 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      <aside className={`fixed left-0 top-0 h-full w-64 bg-gradient-to-b from-[#145f66] to-[#1B919B] z-40 transform transition-transform duration-200 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="h-16 border-b border-white/10 flex items-center justify-center px-4">
          {logoImage ? (
            <div className="w-[200px] h-[48px] overflow-hidden flex items-center justify-center">
              <img src={logoImage} alt="Logo" className="max-w-full max-h-full object-contain brightness-0 invert"
                onError={() => { setLogoImage(null); localStorage.removeItem('cached_logo_url'); }}
                data-testid="sidebar-logo" />
            </div>
          ) : (
            <span className="font-bold text-lg text-white tracking-tight">Lomas Bayas</span>
          )}
        </div>

        <nav className="px-3 py-4 space-y-1">
          {navItems.map(item => {
            if (item.isGroup) {
              const isChildActive = item.children?.some(c => location.pathname.startsWith(c.href));
              const isOpen = openGroups[item.name] !== undefined ? openGroups[item.name] : isChildActive;
              return (
                <div key={item.name}>
                  <button onClick={() => setOpenGroups(g => ({ ...g, [item.name]: !isOpen }))}
                    className="flex items-center justify-between w-full px-3 py-2.5 rounded-lg text-sm text-white/70 hover:bg-white/10 hover:text-white transition-colors">
                    <span className="flex items-center gap-3"><item.icon className="w-4.5 h-4.5" />{item.name}</span>
                    {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                  </button>
                  {isOpen && (
                    <div className="ml-4 mt-1 space-y-0.5">
                      {item.children.map(child => {
                        const childActive = location.pathname.startsWith(child.href);
                        return (
                          <Link key={child.href} to={child.href} onClick={() => setSidebarOpen(false)}
                            className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                              childActive ? 'bg-white/20 text-white font-medium' : 'text-white/50 hover:bg-white/10 hover:text-white'
                            }`}><child.icon className="w-3.5 h-3.5" />{child.name}</Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }
            const active = item.href === '/planificador' ? location.pathname === '/planificador' : location.pathname.startsWith(item.href);
            return (
              <Link key={item.href} to={item.href} onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  active ? 'bg-white/20 text-white font-medium' : 'text-white/70 hover:bg-white/10 hover:text-white'
                }`} data-testid={`nav-${item.name.toLowerCase()}`}>
                <item.icon className="w-4.5 h-4.5" />
                {item.name}
              </Link>
            );
          })}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 px-3 py-4 border-t border-white/10">
          <div className="px-3 py-2 mb-2">
            <p className="text-xs text-white/80">{user?.nombre} {user?.apellido}</p>
            <p className="text-[11px] text-white/40 truncate">{user?.email}</p>
          </div>
          <button onClick={() => { navigate('/seleccionar-rol'); }}
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-white/60 hover:bg-white/10 hover:text-white w-full transition-colors mb-1"
            data-testid="volver-tysta-btn">
            <Grid3X3 className="w-4.5 h-4.5" />
            TystaCore
          </button>
          {hasMultiplePerfiles && hasMultiplePerfiles() && (
            <button
              onClick={() => { if (switchPerfil()) navigate('/seleccionar-perfil'); }}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-white/60 hover:bg-white/10 hover:text-white w-full transition-colors mb-1"
              data-testid="cambiar-perfil-btn">
              <Repeat className="w-4.5 h-4.5" />
              Cambiar de perfil
            </button>
          )}
          <button onClick={logout}
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-white/50 hover:bg-red-500/10 hover:text-red-300 w-full transition-colors"
            data-testid="logout-btn">
            <LogOut className="w-4.5 h-4.5" />
            Cerrar Sesion
          </button>
        </div>
      </aside>

      <div className="lg:pl-64 min-h-screen flex flex-col">
        <header className="sticky top-0 z-30 bg-white border-b border-slate-200 flex items-center justify-between px-4 lg:px-6 h-14">
          <button className="lg:hidden p-2 hover:bg-slate-100 rounded-lg" onClick={() => setSidebarOpen(!sidebarOpen)}>
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
          <div className="hidden lg:block">
            <h1 className="text-lg font-bold text-slate-800">
              {location.pathname.includes('/nueva') ? 'Nueva Actividad' :
               location.pathname.includes('/gantt') ? 'Carta Gantt' :
               location.pathname.includes('/registro') ? 'Registro de Acciones' :
               location.pathname.includes('/areas') ? 'Areas' :
               location.pathname.includes('/sectores') ? 'Sectores' :
               location.pathname.includes('/tipos-actividad') ? 'Tipos de Actividad' :
               location.pathname.includes('/recursos') ? 'Catalogo de Recursos' :
               location.pathname.includes('/personas') ? 'Mantenedor de Personas' :
               location.pathname.includes('/riesgos') ? 'Catalogo de Riesgos y Controles' :
               location.pathname === '/planificador/actividades' ? 'Actividades Operacionales' :
               location.pathname === '/planificador' ? 'Dashboard' : ''}
            </h1>
            {location.pathname === '/planificador/actividades' && (
              <p className="text-xs text-slate-400">Planifique y controle las actividades</p>
            )}
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-[#1B919B] bg-[#1B919B]/10 px-3 py-1 rounded-full">Planificador</span>
          </div>
        </header>
        <main className="flex-1 p-4 lg:px-6 lg:py-4">
          {children}
        </main>
        <footer className="py-4 text-center border-t border-slate-200">
          <p className="text-xs text-slate-400">&copy; TystaCore {new Date().getFullYear()}</p>
        </footer>
      </div>
    </div>
  );
};
